import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Modal, Form, Input, Button, Space, Typography, Card, Tag, Tree, message, Spin, Alert, Select, Divider,
} from 'antd';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';

import {
  loadSchemaUniverse, denialsToKeySet, keysToDenials,
  categorize, schemaKey, viewKey, LAYER_KEYS,
} from './dataAccessTreeUtils';
import { createGroup, getGroup, listGroups, updateGroup } from '../services/dataAccessGroupsService';

const { Text } = Typography;
const { TextArea } = Input;

/**
 * Admin modal: create or edit a Data Access Group.
 *
 * Props:
 *   open      — boolean
 *   groupId   — id of group to edit, or null for "new"
 *   onClose   — fn()
 *   onSaved   — fn(detail) after a successful save
 */
export default function DataAccessGroupModal({ open, groupId, onClose, onSaved }) {
  const isNew = !groupId;
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [schemas, setSchemas] = useState([]);
  const [viewsBySchemaUpper, setViewsBySchemaUpper] = useState({});
  const [allKeys, setAllKeys] = useState([]);
  const [checkedKeys, setCheckedKeys] = useState([]); // checked = DENIED
  const [searchValue, setSearchValue] = useState('');
  const [memberCount, setMemberCount] = useState(0);
  const [isSystem, setIsSystem] = useState(false);

  // Copy-from-group source.
  const [allGroups, setAllGroups] = useState([]);
  const [copyFromGroupId, setCopyFromGroupId] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const universe = await loadSchemaUniverse();
      setSchemas(universe.schemas);
      setViewsBySchemaUpper(universe.viewsBySchemaUpper);
      setAllKeys(universe.allKeys);

      if (isNew) {
        form.resetFields();
        // New group starts with everything ALLOWED (= all checked).
        setCheckedKeys(universe.allKeys);
        setMemberCount(0);
        setIsSystem(false);
      } else {
        const detail = await getGroup(groupId);
        form.setFieldsValue({
          name: detail.name,
          slug: detail.slug,
          description: detail.description || '',
        });
        setMemberCount(detail.member_count || 0);
        // Admins can edit every group, including built-in ones, so we never
        // lock the form here. Keep the value for display purposes only.
        setIsSystem(false);
        // Checked = ALLOWED, so invert the denial set.
        const denialSet = denialsToKeySet(detail.denials, universe.viewsBySchemaUpper);
        setCheckedKeys(universe.allKeys.filter((k) => !denialSet.has(k)));
      }

      // Source list for copy-from dropdown (exclude current group).
      try {
        const groups = await listGroups();
        setAllGroups(Array.isArray(groups) ? groups : []);
      } catch { /* non-fatal */ }
    } catch (e) {
      console.error(e);
      setError(e.message || 'Failed to load group');
    } finally {
      setLoading(false);
    }
  }, [groupId, isNew, form]);

  useEffect(() => {
    if (open) reload();
  }, [open, reload]);

  const buildTreeData = useCallback((category) => {
    const q = (searchValue || '').trim().toLowerCase();
    return schemas
      .filter((s) => categorize(s.schema) === category)
      .map((s) => {
        const sUpper = s.schema.toUpperCase();
        const viewNames = viewsBySchemaUpper[sUpper] || [];
        const children = viewNames
          .filter((v) => !q || v.toLowerCase().includes(q) || s.schema.toLowerCase().includes(q))
          .map((v) => ({ key: viewKey(s.schema, v), title: v }));
        const schemaMatches = !q || s.schema.toLowerCase().includes(q) || children.length > 0;
        if (!schemaMatches) return null;
        return {
          key: schemaKey(s.schema),
          title: (
            <span>
              <strong>{s.schema}</strong>
              <Text type="secondary" style={{ marginLeft: 6, fontSize: 12 }}>
                ({viewNames.length} {viewNames.length === 1 ? 'view' : 'views'})
              </Text>
            </span>
          ),
          children,
        };
      })
      .filter(Boolean);
  }, [schemas, viewsBySchemaUpper, searchValue]);

  const layerTrees = useMemo(() => ({
    RAW: buildTreeData('RAW'),
    REFINED: buildTreeData('REFINED'),
    ANALYTIC: buildTreeData('ANALYTIC'),
  }), [buildTreeData]);

  const keysForCategory = useCallback((category) => {
    const keys = [];
    for (const s of schemas) {
      if (categorize(s.schema) !== category) continue;
      keys.push(schemaKey(s.schema));
      for (const v of (viewsBySchemaUpper[s.schema.toUpperCase()] || [])) {
        keys.push(viewKey(s.schema, v));
      }
    }
    return keys;
  }, [schemas, viewsBySchemaUpper]);

  const handleCheckAllLayer = (category) => {
    const layerKeys = new Set(keysForCategory(category));
    setCheckedKeys((prev) => {
      const next = new Set(prev);
      for (const k of layerKeys) next.add(k);
      return Array.from(next);
    });
  };

  const handleUncheckAllLayer = (category) => {
    const layerKeys = new Set(keysForCategory(category));
    setCheckedKeys((prev) => prev.filter((k) => !layerKeys.has(k)));
  };

  const handleCopyFromGroup = async () => {
    if (!copyFromGroupId) return;
    try {
      const src = await getGroup(copyFromGroupId);
      // Source's denials → invert into allowed (checked).
      const denialSet = denialsToKeySet(src.denials, viewsBySchemaUpper);
      setCheckedKeys(allKeys.filter((k) => !denialSet.has(k)));
      message.success(`Copied access from "${src.name}" — review and click Save.`);
    } catch (e) {
      message.error(e.message || 'Copy failed');
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      // Checked = ALLOWED → denials are the complement.
      const checkedSet = new Set(checkedKeys);
      const deniedKeys = allKeys.filter((k) => !checkedSet.has(k));
      const denials = keysToDenials(deniedKeys, schemas, viewsBySchemaUpper);
      setSaving(true);
      let saved;
      if (isNew) {
        saved = await createGroup({
          name: values.name,
          slug: values.slug || undefined,
          description: values.description || null,
          denials,
        });
      } else {
        saved = await updateGroup(groupId, {
          name: values.name,
          slug: values.slug || undefined,
          description: values.description || null,
          denials,
        });
      }
      message.success(`Group "${saved.name}" saved.`);
      onSaved?.(saved);
      onClose?.();
    } catch (e) {
      if (e?.errorFields) return; // form validation errors already shown
      message.error(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const totals = useMemo(() => {
    let s = 0, t = 0;
    const checkedSet = new Set(checkedKeys);
    for (const sch of schemas) {
      const views = viewsBySchemaUpper[sch.schema.toUpperCase()] || [];
      const allChecked = views.length > 0 && views.every((v) => checkedSet.has(viewKey(sch.schema, v)));
      if (allChecked) {
        s += 1;
      } else {
        for (const v of views) if (checkedSet.has(viewKey(sch.schema, v))) t += 1;
      }
    }
    return { s, t };
  }, [checkedKeys, schemas, viewsBySchemaUpper]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={isNew ? 'New Data Access Group' : 'Edit Data Access Group'}
      width="90vw"
      style={{ top: 24, maxWidth: 1500 }}
      bodyStyle={{ paddingTop: 12 }}
      footer={[
        <Button key="reload" icon={<ReloadOutlined />} onClick={reload} disabled={loading || saving} style={{ float: 'left' }}>
          Refresh
        </Button>,
        <Button key="cancel" onClick={onClose} disabled={saving}>Cancel</Button>,
        <Button key="save" type="primary" loading={saving} disabled={loading || isSystem} onClick={handleSave}>
          {isNew ? 'Create Group' : 'Save Group'}
        </Button>,
      ]}
    >
      {!isNew && memberCount > 0 && (
        <Alert
          type="warning"
          showIcon
          message={`Changes will affect ${memberCount} member${memberCount === 1 ? '' : 's'} currently assigned to this group.`}
          style={{ marginBottom: 12 }}
        />
      )}
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}

      <Spin spinning={loading}>
        <Form form={form} layout="vertical">
          <Space size="middle" style={{ display: 'flex', flexWrap: 'wrap' }}>
            <Form.Item
              name="name"
              label="Group Name"
              rules={[{ required: true, message: 'Name is required' }]}
              style={{ minWidth: 260, flex: 1 }}
            >
              <Input disabled={isSystem} placeholder="e.g. Marketing — Restricted" />
            </Form.Item>
            <Form.Item name="slug" label="Slug (optional)" style={{ minWidth: 200 }}>
              <Input disabled={isSystem} placeholder="auto-generated" />
            </Form.Item>
          </Space>
          <Form.Item name="description" label="Description">
            <TextArea rows={2} disabled={isSystem} placeholder="What kind of users belong here?" />
          </Form.Item>
        </Form>

        <Divider style={{ margin: '8px 0 12px' }} />

        <Space wrap style={{ width: '100%', justifyContent: 'space-between', marginBottom: 12 }}>
          <Space>
            <Tag color="green">Checked = allowed</Tag>
            <Text type="secondary">
              {totals.s} schemas + {totals.t} views allowed
            </Text>
          </Space>
          <Space>
            <Select
              showSearch
              allowClear
              placeholder="Copy access from group…"
              style={{ minWidth: 260 }}
              value={copyFromGroupId}
              onChange={setCopyFromGroupId}
              options={allGroups
                .filter((g) => g.id !== groupId)
                .map((g) => ({ value: g.id, label: g.name }))}
              optionFilterProp="label"
              disabled={isSystem}
            />
            <Button onClick={handleCopyFromGroup} disabled={!copyFromGroupId || isSystem}>Copy</Button>
            <Input
              placeholder="Search schema or view…"
              prefix={<SearchOutlined />}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              allowClear
              style={{ width: 240 }}
            />
          </Space>
        </Space>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          {LAYER_KEYS.map((label) => {
            const treeData = layerTrees[label];
            const color = label === 'RAW' ? 'magenta' : label === 'REFINED' ? 'geekblue' : 'green';
            return (
              <Card
                key={label}
                size="small"
                title={
                  <Space>
                    <Tag color={color} style={{ marginRight: 0 }}>{label}</Tag>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {treeData.length} {treeData.length === 1 ? 'schema' : 'schemas'}
                    </Text>
                  </Space>
                }
                extra={
                  <Space size={4}>
                    <Button size="small" type="link" disabled={isSystem || treeData.length === 0}
                      onClick={() => handleCheckAllLayer(label)}>Allow all</Button>
                    <Button size="small" type="link" danger disabled={isSystem || treeData.length === 0}
                      onClick={() => handleUncheckAllLayer(label)}>Deny all</Button>
                  </Space>
                }
                bodyStyle={{ padding: 8, height: 'calc(75vh - 280px)', overflow: 'auto' }}
                style={{ height: '100%' }}
              >
                {treeData.length === 0 ? (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    No {label.toLowerCase()} schemas.
                  </Text>
                ) : (
                  <Tree
                    checkable
                    selectable={false}
                    treeData={treeData}
                    checkedKeys={checkedKeys}
                    onCheck={(keys) => {
                      const next = Array.isArray(keys) ? keys : (keys?.checked || []);
                      // Each layer renders its own Tree but they share a single
                      // checkedKeys state. AntD only returns the keys for THIS
                      // tree, so we must merge with the keys from the OTHER
                      // layers, otherwise checking one layer wipes the others.
                      const layerSet = new Set(keysForCategory(label));
                      setCheckedKeys((prev) => {
                        const kept = prev.filter((k) => !layerSet.has(k));
                        return [...kept, ...next];
                      });
                    }}
                    disabled={isSystem}
                    defaultExpandAll={false}
                  />
                )}
              </Card>
            );
          })}
        </div>
      </Spin>
    </Modal>
  );
}
